import { platformApiClient } from './platform-auth.service';
import type {
  Tenant,
  CreateTenantDto,
  UpdateTenantDto,
  TenantUsage,
  Subscription,
  PaginatedTenants,
} from '@/types/tenant';

export interface PlatformTenantFilters {
  page?: number;
  limit?: number;
  search?: string;
}

export const platformTenantService = {
  // Tenant CRUD via platform admin endpoints
  async getTenants(filters: PlatformTenantFilters = {}): Promise<PaginatedTenants> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);

    const response = await platformApiClient.get(`/admin/tenants?${params.toString()}`);
    const data = response.data;

    // Normalize response: backend may return { items, total } or flat array
    if (Array.isArray(data)) {
      return { items: data, total: data.length, page: 1, limit: filters.limit || 50, totalPages: 1 } as PaginatedTenants;
    }
    return {
      items: data.items || data.data || [],
      total: data.total || 0,
      page: data.page || 1,
      limit: data.limit || filters.limit || 50,
      totalPages: data.totalPages || 1,
    } as PaginatedTenants;
  },

  async createTenant(data: CreateTenantDto): Promise<Tenant> {
    const response = await platformApiClient.post('/admin/tenants', data);
    return response.data;
  },

  async updateTenant(id: string, data: Partial<UpdateTenantDto>): Promise<Tenant> {
    const response = await platformApiClient.put(`/admin/tenants/${id}`, data);
    return response.data;
  },

  async deleteTenant(id: string): Promise<void> {
    await platformApiClient.delete(`/admin/tenants/${id}`);
  },

  async activateTenant(id: string): Promise<Tenant> {
    const response = await platformApiClient.post(`/admin/tenants/${id}/activate`);
    return response.data;
  },

  async deactivateTenant(id: string): Promise<Tenant> {
    const response = await platformApiClient.post(`/admin/tenants/${id}/deactivate`);
    return response.data;
  },

  async getTenantUsage(id: string): Promise<TenantUsage> {
    const response = await platformApiClient.get(`/admin/tenants/${id}/usage`);
    return response.data;
  },

  async getTenantSubscription(id: string): Promise<Subscription> {
    const response = await platformApiClient.get(`/admin/tenants/${id}/subscription`);
    return response.data;
  },

  async provisionTenantAdmin(tenantId: string, data: { email: string; firstName?: string; lastName?: string }): Promise<any> {
    const response = await platformApiClient.post(`/admin/tenants/${tenantId}/provision-admin`, data);
    return response.data;
  },

  async getTenantUsers(tenantId: string, query?: { search?: string; role?: string; limit?: number }): Promise<{ data: any[]; total: number }> {
    const params = new URLSearchParams();
    if (query?.search) params.append('search', query.search);
    if (query?.role) params.append('role', query.role);
    if (query?.limit) params.append('limit', query.limit.toString());
    const qs = params.toString();
    const response = await platformApiClient.get(`/admin/tenants/${tenantId}/users${qs ? `?${qs}` : ''}`);
    return response.data;
  },
};

export default platformTenantService;
